// src/contexts/PermissionContext.jsx
// Loads the user's organization role permissions on login.
// Admin users (role === 'Admin') bypass all permission checks.
// Supports "preview mode" — admins can temporarily view the app
// as if they had a different role's permissions.

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useUser } from './UserContext';
import { apiService } from '@/services/api';

const PermissionContext = createContext();

// Permissions that default to TRUE when absent from an older role's saved
// permission set — ONLY for keys where failing open is safer than stripping
// core clinical access org-wide on deploy day. Everything else (including
// documents_manage_categories) fails closed: missing = false. Admins bypass
// permissions entirely, so they are never affected either way. Shared with
// RoleManagement so the editor's checkboxes show these effective values.
// Safe to remove once all roles have been re-saved / backfilled
// (backend: functions/backfill-enforced-permissions.xs).
export const ENFORCE_TRUE_IF_MISSING = [
  'clinical_create_notes', 'clinical_sign_notes',
  'clinical_manage_templates', 'clinical_manage_cases',
  'admin_view_audit_log',
  // temporary bridge until backfill_sync_veradigm_permission has run in both envs
  'admin_sync_veradigm'
];

export const PermissionProvider = ({ children }) => {
  const { user } = useUser();
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewRole, setPreviewRole] = useState(null);

  // Load the user's role and permissions when user changes
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user?.id) {
        setRole(null);
        setPermissions(null);
        setLoading(false);
        return;
      }

      // Admin users get all permissions automatically
      if (user.role === 'Admin' || user.role === 'admin') {
        setRole({ name: 'Admin', is_system: true });
        setPermissions(null); // null = admin override, all permissions granted
        setLoading(false);
        return;
      }

      // If user has an org_role_id, fetch it
      if (user.org_role_id && user.organization?.id) {
        try {
          const orgRole = await apiService.getOrganizationRole(user.org_role_id);
          setRole(orgRole);
          const loadedPerms = orgRole.permissions || {};
          for (const key of ENFORCE_TRUE_IF_MISSING) {
            if (loadedPerms[key] === undefined) {
              loadedPerms[key] = true;
            }
          }
          setPermissions(loadedPerms);
        } catch (err) {
          console.error('[PermissionContext] Failed to load role:', err);
          // Fallback: grant basic permissions
          setRole(null);
          setPermissions({});
        }
      } else {
        // No custom role assigned — use basic defaults for 'User' role
        setRole(null);
        setPermissions({});
      }

      setLoading(false);
    };

    setLoading(true);
    loadPermissions();
  }, [user?.id, user?.role, user?.org_role_id, user?.organization?.id]);

  // Clear preview when user changes (e.g., logout)
  useEffect(() => {
    setPreviewRole(null);
  }, [user?.id]);

  const isPreviewing = !!previewRole;

  const startPreview = useCallback(async (roleId) => {
    try {
      const orgRole = await apiService.getOrganizationRole(roleId);
      setPreviewRole(orgRole);
    } catch (err) {
      console.error('[PermissionContext] Failed to load preview role:', err);
    }
  }, []);

  const stopPreview = useCallback(() => {
    setPreviewRole(null);
  }, []);

  // True for Admin users when NOT previewing
  const effectiveIsAdmin = !previewRole && (user?.role === 'Admin' || user?.role === 'admin');

  // When previewing, expose the preview role's data; otherwise the real data
  const effectiveRole = previewRole || role;
  const effectivePermissions = previewRole ? (previewRole.permissions || {}) : permissions;

  /**
   * Check if the current user has a specific permission.
   * Admin users always return true — unless previewing another role.
   * @param {string} permission - Permission key (e.g., 'billing_view_all')
   * @returns {boolean}
   */
  const can = useCallback((permission) => {
    if (previewRole) {
      return !!previewRole.permissions?.[permission];
    }
    // Existing logic — unchanged
    if (user?.role === 'Admin' || user?.role === 'admin') return true;
    if (permissions === null) return true; // admin or not loaded yet
    return !!permissions[permission];
  }, [user?.role, permissions, previewRole]);

  /**
   * Check if the current user has ANY of the specified permissions.
   * @param {string[]} perms - Array of permission keys
   * @returns {boolean}
   */
  const canAny = useCallback((perms) => {
    if (previewRole) {
      return perms.some(p => !!previewRole.permissions?.[p]);
    }
    if (user?.role === 'Admin' || user?.role === 'admin') return true;
    if (permissions === null) return true;
    return perms.some(p => !!permissions[p]);
  }, [user?.role, permissions, previewRole]);

  /**
   * Check if the current user has ALL of the specified permissions.
   * @param {string[]} perms - Array of permission keys
   * @returns {boolean}
   */
  const canAll = useCallback((perms) => {
    if (previewRole) {
      return perms.every(p => !!previewRole.permissions?.[p]);
    }
    if (user?.role === 'Admin' || user?.role === 'admin') return true;
    if (permissions === null) return true;
    return perms.every(p => !!permissions[p]);
  }, [user?.role, permissions, previewRole]);

  /**
   * Check whether a role-level restriction is active for the current user.
   * Restriction keys (restrict_copy, restrict_print, restrict_export,
   * restrict_screenshot) are inverted permissions: an absent or false key
   * means unrestricted, so this fails open. Admins are never restricted
   * unless they are previewing a role that carries the restriction.
   * @param {string} key - Restriction key (e.g., 'restrict_export')
   * @returns {boolean}
   */
  const isRestricted = useCallback((key) => {
    if (effectiveIsAdmin) return false;
    if (!effectivePermissions) return false; // null = admin or not loaded yet
    return effectivePermissions[key] === true;
  }, [effectiveIsAdmin, effectivePermissions]);

  const value = useMemo(() => ({
    role: effectiveRole,
    permissions: effectivePermissions,
    loading,
    can,
    canAny,
    canAll,
    isRestricted,
    effectiveIsAdmin,
    isPreviewing,
    previewRole,
    startPreview,
    stopPreview,
  }), [effectiveRole, effectivePermissions, loading, can, canAny, canAll, isRestricted, effectiveIsAdmin, isPreviewing, previewRole, startPreview, stopPreview]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
